import { useEffect, useState } from "react";
import { api, authedUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users } from "lucide-react";
import ChatPane from "@/components/chat/ChatPane";

/** Group chat for every member of a project — the project's "Discussion" tab. */
export default function ProjectDiscussion({ projectId }) {
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [members, setMembers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setConv(null); setError(null);
    Promise.all([
      api.get(`/chat/projects/${projectId}/conversation`),
      api.get(`/chat/projects/${projectId}/members`),
      api.get("/chat/contacts").catch(() => ({ data: [] })),
    ]).then(([c, m, ct]) => {
      if (cancelled) return;
      setConv(c.data); setMembers(m.data); setContacts(ct.data);
    }).catch((e) => {
      if (!cancelled) setError(e?.response?.data?.detail || "Could not load the discussion");
    });
    return () => { cancelled = true; };
  }, [projectId]);

  if (error) return <div className="text-sm text-muted-foreground p-6 text-center" data-testid="discussion-error">{error}</div>;
  if (!conv) return <div className="text-sm text-muted-foreground p-6 text-center">Loading discussion…</div>;

  return (
    <div className="h-[calc(100vh-260px)] min-h-[460px] flex flex-col rounded-xl hairline overflow-hidden" data-testid="project-discussion">
      <ChatPane
        conversation={conv}
        user={user}
        contacts={contacts}
        members={members}
        isGroup
        className="flex-1"
        headerLeft={({ typingLabel }) => (
          <>
            <div className="h-9 w-9 shrink-0 rounded-full bg-primary/15 text-primary flex items-center justify-center"><Users className="w-4 h-4" /></div>
            <div className="min-w-0">
              <div className="font-medium truncate" data-testid="discussion-title">{conv.title} · Discussion</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {typingLabel || `${members.length} member${members.length === 1 ? "" : "s"} · ${members.map((m) => m.full_name).slice(0, 4).join(", ")}${members.length > 4 ? "…" : ""}`}
              </div>
            </div>
          </>
        )}
      />
    </div>
  );
}
